import React from 'react';
import { motion } from 'motion/react';
import { ArrowRight, Play, MessageCircle } from 'lucide-react';
import Section from '../layout/Section';
import Button from '../ui/Button';
import TextReveal from '../animations/TextReveal';
import ScrollReveal from '../animations/ScrollReveal';

const steps = [
  { icon: '📝', title: 'Inscription', description: '5 minutes' },
  { icon: '⚙️', title: 'Configuration', description: 'Produits & équipe' },
  { icon: '🛒', title: 'Première vente', description: 'Scannez, encaissez' },
  { icon: '📊', title: 'Analysez', description: 'Vos rapports' },
];

export default function CTASection() {
  return (
    <Section dark className="relative">
      {/* Central glow */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px] animate-glow-pulse" />
      </div>

      <div className="relative z-10 text-center max-w-4xl mx-auto">
        {/* How It Works - 4 Steps */}
        <ScrollReveal>
          <p className="text-xs text-gray-500 uppercase tracking-widest mb-3">
            Prêt en 4 étapes
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-4 sm:mb-6">
            {steps.map((step, i) => (
              <div key={i} className="relative">
                <div className="glass rounded-xl p-4 text-center">
                  <div className="text-2xl mb-1">{step.icon}</div>
                  <div className="text-white font-bold text-sm">{step.title}</div>
                  <p className="text-gray-500 text-xs mt-1">{step.description}</p>
                </div>
                {i < steps.length - 1 && (
                  <div className="hidden sm:block absolute top-1/2 -right-3 -translate-y-1/2 text-gray-600 z-10">
                    →
                  </div>
                )}
              </div>
            ))}
          </div>
        </ScrollReveal>

        {/* FOMO Urgency Block */}
        <ScrollReveal>
          <div className="glass rounded-xl p-5 mb-4 sm:mb-6 border border-red-500/20 bg-red-500/5">
            <p className="text-white font-bold text-base mb-3">
              Chaque jour sans XamXamBoutik, c'est :
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3 mb-3">
              <span className="text-gray-400 text-sm">💸 Des dettes oubliées</span>
              <span className="text-gray-400 text-sm">🧾 Des erreurs de caisse</span>
              <span className="text-gray-400 text-sm">📦 Des ruptures de stock</span>
              <span className="text-gray-400 text-sm">⏳ Du temps perdu à recompter</span>
            </div>
            <p className="text-primary font-semibold text-sm">
              Vos concurrents se modernisent. Et vous ?
            </p>
          </div>
        </ScrollReveal>

        <TextReveal
          as="h2"
          className="text-2xl sm:text-3xl lg:text-5xl font-black gradient-text leading-tight"
        >
          Prêt à transformer votre commerce ?
        </TextReveal>
        <motion.p
          className="text-gray-400 text-lg mt-3 mb-4 sm:mb-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
        >
          Le meilleur moment était hier. Le deuxième, c'est maintenant.
        </motion.p>

        <ScrollReveal delay={0.3}>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
            <div className="glass rounded-xl p-5 text-center hover:border-primary/30 transition-colors">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <ArrowRight className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-white font-bold text-sm mb-1">Essai gratuit</h3>
              <p className="text-gray-400 text-xs mb-3">1 mois gratuit, toutes fonctionnalités</p>
              <Button variant="primary" size="sm" className="w-full" href="https://xamxamshop.com">
                Commencer
              </Button>
            </div>

            <div className="glass rounded-xl p-5 text-center hover:border-primary/30 transition-colors">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <Play className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-white font-bold text-sm mb-1">Démo personnalisée</h3>
              <p className="text-gray-400 text-xs mb-3">Un expert vous montre l'app en 30 min, adaptée à votre commerce</p>
              <Button variant="secondary" size="sm" className="w-full" href="https://xamxamshop.com">
                Planifier
              </Button>
            </div>

            <div className="glass rounded-xl p-5 text-center hover:border-primary/30 transition-colors">
              <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="w-5 h-5 text-primary" />
              </div>
              <h3 className="text-white font-bold text-sm mb-1">WhatsApp</h3>
              <p className="text-gray-400 text-xs mb-3">Parlez-nous directement</p>
              <Button variant="outline" size="sm" className="w-full" href="https://wa.me/221788752020?text=Bonjour%2C%20je%20suis%20int%C3%A9ress%C3%A9%20par%20XamXamBoutik">
                Contacter
              </Button>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </Section>
  );
}
